import React from "react";
//Material imports
import Grid from "@mui/material/Grid";
//Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import { useExpertDashboard } from "@limitless/expert";
import DashboardChart, { defaultChart } from "./DashboardChart";
import DashboardGraphHeader from "./DashboardGraphHeader";
import { commonStyle } from "./DashboardRewards";
import DashboardCommonTooltip from "./DashboardCommonTooltip";
import * as ReactDOMServer from "react-dom/server";
import { useTheme } from "@mui/material/styles";

const DashboardTasks = () => {
    const classes = commonStyle();
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        categories,
        fetchMontlyTasks,
        currentMonthPloting,
        taskTrendRate,
        month,
        group,
        feesEarned,
        status,
        monthSuggestions
    } = useExpertDashboard();

    const getMaxTotal = React.useMemo(() => {
        const data = [];
        for (let i = 0; i < currentMonthPloting?.respond?.length; i++) {
            data.push(
                currentMonthPloting.respond[i].value +
                    currentMonthPloting.escalate[i].value +
                    currentMonthPloting.review[i].value
            );
        }

        return Math.max(...data);
    }, [currentMonthPloting]);

    const series = React.useMemo(() => {
        return [
            {
                name: t("responded"),
                data: currentMonthPloting.respond
            },
            {
                name: t("forwarded"),
                data: currentMonthPloting.escalate
            },
            {
                name: t("reviewed"),
                data: currentMonthPloting.review
            }
        ];
    }, [currentMonthPloting]);

    React.useEffect(() => {
        if (Object.keys(monthSuggestions).length !== 0) {
            fetchMontlyTasks(month, group);
        }
    }, [monthSuggestions]);

    const defaultYaxisValue = {
        labels: {
            formatter(val) {
                return Math.round(val);
            },
            style: {
                colors: theme.palette.text.secondary
            },
            offsetX: 0
        },
        opposite: true,
        type: "numeric",
        tickAmount: 4
    };
    const DefaultYaxis =
        getMaxTotal < 5
            ? {
                  ...defaultYaxisValue,
                  max: 4
              }
            : {
                  ...defaultYaxisValue
              };
    const options = {
        chart: {
            ...defaultChart,
            stacked: true
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: `${(2 + (60 / (1 + (30*Math.exp(-categories.length /3)))))}%`
            }
        },
        yaxis: DefaultYaxis,
        tooltip: {
            shared: true,
            intersect: false,
            theme: true,
            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                let currentTotal = 0;
                const category = [];
                series.forEach(s => {
                    category.push(s[dataPointIndex]);
                    currentTotal += s[dataPointIndex];
                });

                return ReactDOMServer.renderToStaticMarkup(
                    <DashboardCommonTooltip
                        className="task_arrow_box"
                        date={data.date}
                        value={currentTotal}
                        theme={theme}
                        valueText={t("completed tasks")}
                        category={category}
                        categoryLabel={[t("responded"), t("forwarded"), t("reviewed")]}
                        colorsGrid={w.globals.colors}
                    />
                );
            }
        },
        fill: {
            type: "solid"
        }
    };

    return (
        <Grid container className={classes.container}>
            <DashboardGraphHeader
                title="Completed tasks"
                fetchMontlyData={fetchMontlyTasks}
                leftTitle={feesEarned}
                status={status}
                rate={Math.abs(taskTrendRate).toFixed(2)}
            />

            <Grid className="chart">
                <DashboardChart
                    series={series}
                    categories={categories}
                    chartType="bar"
                    crosshairsShown={false}
                    options={options}
                />
            </Grid>
        </Grid>
    );
};

export default DashboardTasks;
